import { Box, Divider, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AddButton from "../../../../common/components/functional/addButton/AddButton";
import { SpinnerButton } from "../../../../common/components/functional/spinnerButton/SpinnerButton";
import useIsLoading from "../../../../hooks/useIsLoading";
import { OrderType } from "../../orderAPI";
import { FormValueTypes } from "../../types/longOrderTypes";
import { RowCard } from "./RowCard";
import { convertDateToYYYYMMDDString } from "../../../../app/common";

type Props = {
  rows: FormValueTypes[];
  addRow: () => void;
  changeRow: (uid: string) => void;
  saveOrder: () => void;
};

export const RowList = ({ rows, addRow, changeRow, saveOrder }: Props) => {
  const { t } = useTranslation("order");
  const isLoading = useIsLoading(["orderRows"]);

  const { watch, formState } = useFormContext();

  // TODO would need to implement useFieldArray to track the state easier?
  const requiredMissing = rows.some(
    (row) =>
      Object.keys(formState.errors).length ||
      !row.workLocation?.id ||
      !row.orderTemplate?.id ||
      row.customerCostCenters.length === 0 ||
      !row.shortWorkTimeDescriptionFi ||
      !row.workDescriptionFi ||
      !row.workStarts ||
      (row.type === OrderType.FixedTerm &&
        (!row.workEnds || !row.reasonForTemporaryEmployment)),
  );

  const submitDisabled = formState.isDirty || requiredMissing || isLoading;

  return (
    <Box mr={1.5}>
      {rows.map((row, index) => {
        const workStartEnd = `${row.workStarts ? convertDateToYYYYMMDDString(row.workStarts, ".", true) : ""} - ${
          row.workEnds
            ? convertDateToYYYYMMDDString(row.workEnds, ".", true)
            : ""
        }`;
        return (
          <RowCard
            key={index}
            templateName={row.orderTemplate?.workTitleId || ""}
            workLocationName={row.workLocation?.name || ""}
            workStartEnd={workStartEnd}
            amount={row.amount}
            selectRow={() => changeRow(row.uid)}
            isSelected={row.uid === watch("uid")}
          />
        );
      })}

      <AddButton
        label={t("order:orderRow")}
        variant="outlined"
        onClick={addRow}
        sx={{ height: "36px", borderRadius: "20px" }}
      />
      <Divider sx={{ marginTop: 4, marginBottom: 2 }} />
      <Typography fontSize={14}>{t("saveLongOrderInfo")}</Typography>
      <Box mb={1.5}>
        <SpinnerButton
          color="primary"
          variant="contained"
          onClick={saveOrder}
          label={t("order:sendOrder")}
          isLoading={isLoading}
          disabled={submitDisabled}
          sx={{
            height: "36px",
            borderRadius: "20px",
            mt: 2,
          }}
        />
      </Box>
      <Typography variant="secondary" fontSize={12}>
        {t("saveOrderBeforeCanSend")}
      </Typography>
    </Box>
  );
};
